import { useIFetch } from '@/composables/useIFetch';
import apiRoutes from '@/const/apiRoutes';
import { useCurrencyStore } from './currency.pinia';
import type { pricesDataResponse } from '~/types/pricesDataResponse';
import type { TherapistRecommendationsResponse } from '~/types/therapist-recommendations';

export const usePlanStore = defineStore('planStore', () => {
   const { setSnackbarDetail } = useSnackbarStore();
   const { track } = useSegment();
   const { currentCurrency } = storeToRefs(useCurrencyStore());
   const { getSelectedTherapistInfo } = useClientInfo();
   const recommendedList: Ref<{
      data: TherapistRecommendationsResponse[];
      meta: { total: number; per_page?: number };
   } | null> = ref(null);
   const getRecommendedList = async (params: { page: number }) => {
      const { data, error } = await useIFetch<{
         data: TherapistRecommendationsResponse[];
         meta: { total: number; per_page?: number };
      } | null>(apiRoutes?.client?.therapistRecomendation, {
         params,
      });
      if (data?.value) {
         if (params.page == 1) recommendedList.value = data?.value;
         else {
            recommendedList.value = {
               data: [
                  ...(recommendedList.value?.data || []),
                  ...(data?.value?.data || []),
               ],
               meta: data?.value.meta,
            };
         }
      }
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: 'Something went wrong',
            type: 'error',
         });
      }
   };
   const selectTherapistRecommended = async (therapistId: string) => {
      const { data, error } = await useIFetch(
         apiRoutes.client?.therapistSelect(therapistId),
         {
            method: 'POST',
         }
      );
      if (data?.value) {
         const { clientInformation, clientProfile } = useClientInfo();
         track('Therapist Selected', {
            email: clientInformation?.client?.email,
            sponsor: clientProfile?.sponsor,
            therapist_id: therapistId,
         });

         await getSelectedTherapistInfo();
      }
      // if (error?.value) {
      //       setSnackbarDetail({
      //          type: 'error',
      //          show: true,
      //          title: error?.value?.data?.message,
      //       });
      // }
      return { data, error };
   };

   // Returns the quote to the caller instead of caching it: each screen keeps
   // its own list so concurrent requests for different types cannot overwrite
   // the prices another screen is showing.
   const getPrices = async (type: string) => {
      const acquisition = useAcquisition();
      const { data, error } = await useIFetch<pricesDataResponse>(
         apiRoutes.stripe?.prices,
         {
            query: {
               type,
               currency: currentCurrency.value.code,
            },
         }
      );
      if (!error?.value && data?.value) {
         await acquisition.refreshClientAcquisition();
         return data?.value;
      }
      if (error?.value) {
         setSnackbarDetail({
            type: 'error',
            show: true,
            title: error?.value?.data?.message,
         });
         return error?.value;
      }
   };
   return {
      getRecommendedList,
      recommendedList,
      selectTherapistRecommended,
      getPrices,
   };
});
